import { useEffect, useMemo, useRef, useState } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import type { AuthSession } from '../../../../types/plans'
import {
  analyzePresentationPhotos,
  fetchGammaThemes,
  fetchNasFolderBrowse,
  fetchPresentationPhotoSources,
  fetchPresentationStatus,
  generatePresentation,
  type GammaTheme,
  type PhotoLayoutResult,
  type NasFolderBrowseResult,
  type PresentationPhotoSource,
  type PresentationPhotoSources,
} from '../diagnostics/planPresentation'
import { groupPresentationPhotos } from './groupPresentationPhotos'
import { PresentationPhotoCard } from './PresentationPhotoCard'
import { useGammaPhotoSelection } from './useGammaPhotoSelection'
import { savePlanWorkflowCheckpoint, WORKFLOW_CHECKPOINT_KEYS } from '../workflowCheckpoint'

const POLL_MS = 4000

type StaffMediaItem = {
  id: number
  original_filename?: string
  filename?: string
  mime_type?: string
  thumb_url?: string | null
  medium_url?: string | null
  large_url?: string | null
  url?: string | null
}

function staffMediaToPhoto(item: StaffMediaItem): PresentationPhotoSource {
  const displayName = item.original_filename || item.filename || `media-${item.id}`
  const previewUrl = item.thumb_url || item.medium_url || item.large_url || item.url || ''
  return {
    photo_id: `staff-media:${item.id}`,
    source: 'media_library',
    display_name: displayName,
    mime_type: item.mime_type || 'image/jpeg',
    size_bytes: 0,
    preview_url: previewUrl,
    thumbnail_path: '',
    source_key: 'staff-media',
    source_label: 'Медиатека',
  }
}

type SavedGammaPresentation = {
  generation_id: string
  gamma_url: string
  created_at: string
  photo_count: number
  selected_photo_ids: string[]
  theme_id?: string | null
  prompt?: string | null
  plan_id: string
}

function browseAssetToPhoto(sourceKey: string, sourceLabel: string, asset: NasFolderBrowseResult['assets'][number]): PresentationPhotoSource {
  return {
    photo_id: `nas:${sourceKey}:${asset.asset_id}`,
    source: 'patient_media',
    display_name: asset.asset_name,
    mime_type: asset.mime_type,
    size_bytes: 0,
    preview_url: asset.preview_url,
    thumbnail_path: '',
    source_key: sourceKey,
    source_label: sourceLabel,
  }
}

function folderSegments(folderPath: string): Array<{ label: string; path: string }> {
  if (!folderPath) return [{ label: 'Корень', path: '' }]
  const parts = folderPath.split('/').filter(Boolean)
  const out: Array<{ label: string; path: string }> = [{ label: 'Корень', path: '' }]
  let acc = ''
  for (const part of parts) {
    acc = acc ? `${acc}/${part}` : `/${part}`
    out.push({ label: part, path: acc })
  }
  return out
}

const MAX_POLLS = 60

export function GammaPhotoPickerPanel({
  bootstrap,
  session,
  misPatientId,
  planId,
  constructorPlanId = null,
  initialPresentations = [],
}: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  misPatientId: string
  planId: string
  constructorPlanId?: number | null
  initialPresentations?: SavedGammaPresentation[]
}) {
  const [sources, setSources] = useState<PresentationPhotoSources | null>(null)
  const [loadError, setLoadError] = useState('')
  const [loading, setLoading] = useState(true)
  const [activeGroupKey, setActiveGroupKey] = useState<string>('plan')
  const [photoLayout, setPhotoLayout] = useState<PhotoLayoutResult | null>(null)
  const [themes, setThemes] = useState<GammaTheme[]>([])
  const [themesError, setThemesError] = useState('')
  const [selectedThemeId, setSelectedThemeId] = useState('')
  const [customPrompt, setCustomPrompt] = useState('')
  const [busy, setBusy] = useState<'analyze' | 'generate' | null>(null)
  const [polling, setPolling] = useState(false)
  const [gammaUrl, setGammaUrl] = useState<string | null>(null)
  const [savedPresentations, setSavedPresentations] = useState<SavedGammaPresentation[]>(initialPresentations)
  const [genError, setGenError] = useState('')
  const [lightboxPhoto, setLightboxPhoto] = useState<PresentationPhotoSource | null>(null)
  const pollRef = useRef(0)

  const [nasBrowsePath, setNasBrowsePath] = useState<Record<string, string>>({})
  const [nasBrowseData, setNasBrowseData] = useState<Record<string, NasFolderBrowseResult | null>>({})
  const [nasBrowseLoading, setNasBrowseLoading] = useState<string | null>(null)
  const [mediaLibraryPhotos, setMediaLibraryPhotos] = useState<PresentationPhotoSource[]>([])
  const [mediaLibraryError, setMediaLibraryError] = useState('')

  useEffect(() => {
    setSavedPresentations(initialPresentations)
  }, [initialPresentations])

  const {
    selectedIds,
    selectedSet,
    selectedCount,
    toggle,
    setGroupSelection,
    selectAll,
    clearAll,
    isGroupFullySelected,
    resetSelection,
  } = useGammaPhotoSelection()

  useEffect(() => {
    let cancelled = false
    void (async () => {
      setLoading(true)
      setLoadError('')
      try {
        const data = await fetchPresentationPhotoSources({
          bootstrap,
          session,
          patientId: misPatientId,
          planId,
        })
        if (cancelled) return
        setSources(data)
        const all = [...data.plan_photos, ...data.nas_photos]
        resetSelection(all.slice(0, 8).map((p) => p.photo_id))
        const groups = groupPresentationPhotos(data)
        setActiveGroupKey(groups[0]?.key ?? 'plan')
      } catch (e) {
        if (!cancelled) setLoadError(String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [bootstrap, session, misPatientId, planId])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      setMediaLibraryError('')
      try {
        const res = await fetch(`${bootstrap.apiBase}/staff/media/list?file_type=image&per_page=48`, {
          headers: { Authorization: `Bearer ${session.accessToken}` },
        })
        if (!res.ok) throw new Error(`media-library ${res.status}`)
        const data = (await res.json()) as { items?: StaffMediaItem[] }
        if (!cancelled) setMediaLibraryPhotos((data.items ?? []).map(staffMediaToPhoto))
      } catch (e) {
        if (!cancelled) setMediaLibraryError(String(e))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [bootstrap.apiBase, session.accessToken])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const items = await fetchGammaThemes({ bootstrap, session })
        if (!cancelled) setThemes(items)
      } catch (e) {
        if (!cancelled) setThemesError(String(e))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [bootstrap, session])

  const groups = useMemo(() => {
    const base = sources ? groupPresentationPhotos(sources) : []
    if (!mediaLibraryPhotos.length) return base
    return [
      { key: 'staff-media', label: 'Медиатека', kind: 'media_library' as const, photos: mediaLibraryPhotos },
      ...base,
    ]
  }, [sources, mediaLibraryPhotos])
  const allPhotos = useMemo(
    () => [...mediaLibraryPhotos, ...(sources?.plan_photos ?? []), ...(sources?.nas_photos ?? [])],
    [mediaLibraryPhotos, sources],
  )
  const activeGroup = useMemo(
    () => groups.find((g) => g.key === activeGroupKey) ?? groups[0] ?? null,
    [groups, activeGroupKey],
  )
  const browsePhotos = useMemo(() => {
    if (!activeGroup || activeGroup.kind !== 'nas') return null
    const browse = nasBrowseData[activeGroup.key]
    if (!browse?.assets?.length) return activeGroup.photos
    return browse.assets.map((asset) => browseAssetToPhoto(activeGroup.key, activeGroup.label, asset))
  }, [activeGroup, nasBrowseData])

  const visiblePhotos = browsePhotos ?? activeGroup?.photos ?? allPhotos

  useEffect(() => {
    const group = groups.find((g) => g.key === activeGroupKey)
    if (!group || group.kind !== 'nas') return
    const folderPath = nasBrowsePath[group.key] ?? ''
    let cancelled = false
    void (async () => {
      setNasBrowseLoading(group.key)
      try {
        const data = await fetchNasFolderBrowse({
          bootstrap,
          session,
          patientId: misPatientId,
          sourceKey: group.key,
          folderPath,
        })
        if (!cancelled) setNasBrowseData((prev) => ({ ...prev, [group.key]: data }))
      } catch {
        if (!cancelled) setNasBrowseData((prev) => ({ ...prev, [group.key]: null }))
      } finally {
        if (!cancelled) setNasBrowseLoading(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [activeGroupKey, groups, nasBrowsePath, bootstrap, session, misPatientId])


  const onToggle = (photoId: string) => {
    setPhotoLayout(null)
    toggle(photoId)
  }

  const analyze = async () => {
    if (!selectedIds.length) return
    setBusy('analyze')
    setGenError('')
    try {
      const result = await analyzePresentationPhotos({
        bootstrap,
        session,
        patientId: misPatientId,
        planId,
        photoIds: selectedIds,
      })
      setPhotoLayout(result)
    } catch (e) {
      setGenError(String(e))
    } finally {
      setBusy(null)
    }
  }

  const persistGammaPresentation = async (record: SavedGammaPresentation) => {
    const next = [record, ...savedPresentations.filter((item) => item.generation_id !== record.generation_id)].slice(0, 20)
    setSavedPresentations(next)
    if (!constructorPlanId) return
    await savePlanWorkflowCheckpoint(bootstrap.apiBase, session, constructorPlanId, WORKFLOW_CHECKPOINT_KEYS.gammaPresentation, {
      step: 'completed',
      presentations: next,
      generation_id: record.generation_id,
      gamma_url: record.gamma_url,
    })
  }

  const generate = async () => {
    if (!selectedIds.length) return
    setBusy('generate')
    setGenError('')
    setGammaUrl(null)
    try {
      const selectedMediaUrls = mediaLibraryPhotos
        .filter((photo) => selectedSet.has(photo.photo_id) && photo.preview_url)
        .map((photo) => photo.preview_url as string)
      const { generation_id } = await generatePresentation({
        bootstrap,
        session,
        patientId: misPatientId,
        planId,
        selectedPhotoIds: selectedIds.filter((id) => !id.startsWith('staff-media:')),
        mediaUrls: selectedMediaUrls,
        photoLayout,
        customPrompt: customPrompt.trim() || undefined,
        themeId: selectedThemeId || undefined,
      })
      setPolling(true)
      pollRef.current = 0
      const poll = async () => {
        pollRef.current += 1
        if (pollRef.current > MAX_POLLS) {
          setPolling(false)
          setGenError('Timeout Gamma')
          return
        }
        const st = await fetchPresentationStatus({ bootstrap, session, generationId: generation_id })
        if (st.status === 'completed' && st.gamma_url) {
          setPolling(false)
          setGammaUrl(st.gamma_url)
          await persistGammaPresentation({
            generation_id,
            gamma_url: st.gamma_url,
            created_at: new Date().toISOString(),
            photo_count: selectedIds.length,
            selected_photo_ids: selectedIds,
            theme_id: selectedThemeId || null,
            prompt: customPrompt.trim() || null,
            plan_id: planId,
          })
          return
        }
        if (st.status === 'failed') {
          setPolling(false)
          setGenError(st.error ?? 'Gamma error')
          return
        }
        window.setTimeout(() => void poll(), POLL_MS)
      }
      void poll()
    } catch (e) {
      setGenError(String(e))
    } finally {
      setBusy(null)
    }
  }

  const disabled = Boolean(busy) || polling

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-1">
      <div className="shrink-0">
        <p className="text-sm font-semibold text-slate-800">Презентация Gamma</p>
        <p className="text-xs text-slate-500">
          Выберите фото с превью. В Gamma уйдёт <strong>{selectedCount}</strong> из {allPhotos.length}.
        </p>
      </div>

      {loading ? <p className="text-xs text-slate-500">Загружаем фото…</p> : null}
      {loadError ? <p className="text-xs text-red-600">{loadError}</p> : null}
      {mediaLibraryError ? <p className="text-xs text-amber-700">Медиатека недоступна: {mediaLibraryError}</p> : null}
      {!loading && allPhotos.length === 0 ? (
        <p className="text-xs text-slate-500">Нет фото в плане и на NAS.</p>
      ) : null}

      {!loading && allPhotos.length > 0 ? (
        <div className="flex min-h-0 flex-1 gap-3">
          <aside className="w-52 shrink-0 space-y-1 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs">
            <p className="mb-1 font-semibold text-slate-700">Источники</p>
            {groups.map((group) => {
              const count = group.photos.length
              const selectedInGroup = group.photos.filter((p) => selectedSet.has(p.photo_id)).length
              return (
                <button
                  key={group.key}
                  type="button"
                  className={`w-full rounded-md px-2 py-1.5 text-left ${
                    activeGroupKey === group.key ? 'bg-indigo-100 text-indigo-900' : 'hover:bg-white'
                  }`}
                  onClick={() => { setActiveGroupKey(group.key); if (group.kind === 'nas' && !nasBrowsePath[group.key]) setNasBrowsePath((prev) => ({ ...prev, [group.key]: '' })) }}
                >
                  <div className="font-medium">{group.label}</div>
                  <div className="text-slate-500">
                    {selectedInGroup}/{count}
                    {group.kind === 'nas' && group.linked === false ? ' · не привязан' : ''}
                    {group.kind === 'nas' && group.available === false ? ' · недоступен' : ''}
                  </div>
                </button>
              )
            })}
          </aside>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-medium text-slate-700">
                {activeGroup?.label ?? 'Все фото'} · выбрано {selectedCount}
              </span>

            {activeGroup?.kind === 'nas' ? (
              <div className="flex flex-wrap items-center gap-1 text-[11px] text-slate-600">
                {folderSegments(nasBrowsePath[activeGroup.key] ?? '').map((seg) => (
                  <button
                    key={seg.path || 'root'}
                    type="button"
                    className="rounded border border-slate-200 bg-white px-1.5 py-0.5 hover:bg-slate-50"
                    onClick={() =>
                      setNasBrowsePath((prev) => ({ ...prev, [activeGroup.key]: seg.path }))
                    }
                  >
                    {seg.label}
                  </button>
                ))}
                {nasBrowseLoading === activeGroup.key ? <span>…</span> : null}
              </div>
            ) : null}
            {activeGroup?.kind === 'nas' && nasBrowseData[activeGroup.key]?.subfolders?.length ? (
              <div className="flex flex-wrap gap-1 text-[11px]">
                {nasBrowseData[activeGroup.key]!.subfolders.map((folder) => (
                  <button
                    key={folder.folder_path}
                    type="button"
                    className="rounded border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-indigo-900 hover:bg-indigo-100"
                    onClick={() =>
                      setNasBrowsePath((prev) => ({ ...prev, [activeGroup.key]: folder.folder_path }))
                    }
                  >
                    📁 {folder.folder_name} ({folder.image_count})
                  </button>
                ))}
              </div>
            ) : null}

              <button
                type="button"
                className="rounded border border-slate-300 bg-white px-2 py-1 disabled:opacity-50"
                disabled={disabled || !activeGroup}
                onClick={() => activeGroup && setGroupSelection(activeGroup.photos, true)}
              >
                Выбрать все в папке
              </button>
              <button
                type="button"
                className="rounded border border-slate-300 bg-white px-2 py-1 disabled:opacity-50"
                disabled={disabled || !activeGroup}
                onClick={() => activeGroup && setGroupSelection(activeGroup.photos, false)}
              >
                Снять в папке
              </button>
              <button
                type="button"
                className="rounded border border-slate-300 bg-white px-2 py-1 disabled:opacity-50"
                disabled={disabled}
                onClick={() => selectAll(allPhotos)}
              >
                Выбрать все
              </button>
              <button
                type="button"
                className="rounded border border-slate-300 bg-white px-2 py-1 disabled:opacity-50"
                disabled={disabled}
                onClick={clearAll}
              >
                Снять все
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                {visiblePhotos.map((photo) => (
                  <PresentationPhotoCard
                    key={photo.photo_id}
                    photo={photo}
                    checked={selectedSet.has(photo.photo_id)}
                    disabled={disabled}
                    bootstrap={bootstrap}
                    accessToken={session.accessToken}
                    onToggle={() => onToggle(photo.photo_id)}
                    onPreview={() => setLightboxPhoto(photo)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="shrink-0 space-y-2 border-t border-slate-200 pt-2">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs disabled:opacity-50"
            disabled={!selectedIds.length || disabled}
            onClick={() => void analyze()}
          >
            {busy === 'analyze' ? 'Анализ…' : 'Распределить фото (AI)'}
          </button>
          <button
            type="button"
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs text-white disabled:opacity-50"
            disabled={!selectedIds.length || disabled}
            onClick={() => void generate()}
          >
            {busy === 'generate' || polling
              ? 'Gamma…'
              : `Создать презентацию (${selectedCount} фото)`}
          </button>
        </div>

        {photoLayout?.assignments?.length ? (
          <div className="max-h-24 space-y-1 overflow-y-auto rounded-md border border-slate-200 bg-white p-2 text-xs">
            {photoLayout.assignments.map((a) => (
              <div key={`${a.photo_id}-${a.stage_index}`}>
                Этап {a.stage_index}: {a.description ?? a.photo_id}
              </div>
            ))}
          </div>
        ) : null}

        {themes.length > 0 ? (
          <label className="grid gap-1 text-xs">
            <span className="font-medium text-slate-700">Тема Gamma</span>
            <select
              className="rounded-md border border-slate-300 bg-white p-2"
              value={selectedThemeId}
              disabled={disabled}
              onChange={(e) => setSelectedThemeId(e.target.value)}
            >
              <option value="">По умолчанию</option>
              {themes.map((theme) => (
                <option key={theme.id} value={theme.id}>
                  {theme.name}
                </option>
              ))}
            </select>
          </label>
        ) : themesError ? (
          <p className="text-xs text-amber-700">Темы Gamma недоступны: {themesError}</p>
        ) : null}

        <textarea
          className="w-full rounded-md border border-slate-300 p-2 text-xs"
          rows={2}
          placeholder="Доп. инструкции для Gamma (стиль клиники подставится автоматически)"
          value={customPrompt}
          disabled={disabled}
          onChange={(e) => setCustomPrompt(e.target.value)}
        />
        {savedPresentations.length ? (
          <div className="max-h-28 overflow-y-auto rounded-md border border-indigo-100 bg-indigo-50/60 p-2 text-xs">
            <p className="mb-1 font-semibold text-indigo-900">Сохранённые Gamma к плану</p>
            <div className="space-y-1">
              {savedPresentations.map((item) => (
                <div key={item.generation_id} className="flex items-center justify-between gap-2 rounded bg-white px-2 py-1">
                  <span className="min-w-0 truncate text-slate-600">
                    {new Date(item.created_at).toLocaleString('ru-RU')} · {item.photo_count} фото
                  </span>
                  <a href={item.gamma_url} target="_blank" rel="noopener noreferrer" className="shrink-0 font-medium text-indigo-700 underline">
                    Открыть
                  </a>
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {gammaUrl ? (
          <a
            href={gammaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-medium text-indigo-700 underline"
          >
            Открыть в Gamma
          </a>
        ) : null}
        {genError ? <p className="text-xs text-red-600">{genError}</p> : null}
      </div>

      {lightboxPhoto ? (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 p-4"
          role="dialog"
          onClick={() => setLightboxPhoto(null)}
        >
          <div
            className="max-h-[90vh] max-w-4xl overflow-hidden rounded-lg bg-white p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <PresentationPhotoCard
              photo={lightboxPhoto}
              checked={selectedSet.has(lightboxPhoto.photo_id)}
              disabled={disabled}
              bootstrap={bootstrap}
              accessToken={session.accessToken}
              onToggle={() => onToggle(lightboxPhoto.photo_id)}
            />
            <button
              type="button"
              className="mt-2 w-full rounded border px-3 py-2 text-sm"
              onClick={() => setLightboxPhoto(null)}
            >
              Закрыть
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
